import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "grid w-full grid-cols-1 sm:grid-cols-2" }
const _hoisted_2 = { class: "flex w-full flex-col items-center bg-gray-50 px-8 pt-20 sm:items-start sm:justify-center sm:pt-0 lg:px-28 xl:px-40" }
const _hoisted_3 = {
  key: 0,
  class: "mb-8 text-2xl font-semibold text-gray-700"
}
const _hoisted_4 = {
  key: 1,
  class: "mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4"
}
const _hoisted_5 = { class: "flex" }
const _hoisted_6 = { class: "shrink-0" }
const _hoisted_7 = { class: "ml-3" }
const _hoisted_8 = { class: "text-sm text-yellow-700" }
const _hoisted_9 = { class: "mb-3 text-2xl font-semibold text-gray-700" }
const _hoisted_10 = { class: "mb-8 text-sm" }
const _hoisted_11 = { class: "mb-3 text-2xl font-semibold text-gray-700" }
const _hoisted_12 = { class: "mb-8 text-sm" }
const _hoisted_13 = { class: "flex items-center justify-between" }
const _hoisted_14 = { class: "text-sm" }
const _hoisted_15 = { class: "block" }
const _hoisted_16 = { class: "flex flex-row-reverse text-sm" }
const _hoisted_17 = { class: "block" }
const _hoisted_18 = { key: 6 }
const _hoisted_19 = { class: "block" }

import { computed, ComputedRef, reactive, ref, Ref } from 'vue';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { email, maxLength, required, requiredIf } from '@/utils/validators';
import { ExclamationIcon } from '@heroicons/vue/solid';

import router from '@/router';
import { useAuthStore, useUiStore } from '@/stores';
import AuthService from '@/services/api/AuthService';
import { UserService } from '@/services';
import { toastServiceError } from '@/services/core/notification';

import { AuthServiceLoginResult, LoginFormState, UserModel } from '@/types';
import axios from 'axios';
enum FormType {
    LOGIN = 'LOGIN',
    RECOVER = 'RECOVER',
    SENT_RECOVER = 'SENT_RECOVER',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LoginPage',
  setup(__props) {

const uiStore = useUiStore();
const authStore = useAuthStore();

const showMessageUserBlocked = ref(false);

const formState = reactive<LoginFormState>({
    email: '',
    password: '',
    rememberMe: true,
});

const showFormType = ref<FormType>(FormType.LOGIN);

const user: ComputedRef<UserModel | undefined> = computed(() => authStore.userModel);

const rules = computed<ValidationArgs<LoginFormState>>(() => ({
    email: {
        email,
        required,
        maxLength: maxLength(250),
    },
    password: {
        required: requiredIf(() => showFormType.value === FormType.LOGIN),
    },
    rememberMe: {},
}));

const v$: Ref<Validation<ValidationArgs<LoginFormState>>> = useVuelidate(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const handleFormValidation = () => v$.value.$validate();

const handleLoginSuccess = async (response: AuthServiceLoginResult) => {
    authStore.login(response);

    if (user.value !== undefined) {
        if (authStore.userNeedsUpdatePassword) {
            await router.push({ name: 'UpdatePassword' });
            return;
        }

        if (user.value.requiresTwoFactor) {
            await router.push({ name: 'TwoFaAuthenticator' });
            return;
        }
    }
    await router.push('/');
};

const onSubmitLogin = async () => {
    if (!(await handleFormValidation())) return;

    try {
        setLoading(true);
        const response = await AuthService.login(formState);
        await handleLoginSuccess(response.data);
    } catch (error) {
        if (axios.isAxiosError(error) && error?.response?.status === 423) {
            showMessageUserBlocked.value = true;
        } else {
            toastServiceError(error);
        }
    } finally {
        setLoading(false);
    }
};

const onSubmitResetPassword = async () => {
    if (!(await handleFormValidation())) return;

    try {
        setLoading(true);
        await UserService.recoveryPassword(formState);
        showFormType.value = FormType.SENT_RECOVER;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

return (_ctx: any,_cache: any) => {
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiLoadingDialog = _resolveComponent("UiLoadingDialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[9] || (_cache[9] = _createElementVNode("div", { class: "bg-primary relative hidden w-full items-center justify-center sm:flex" }, [
        _createElementVNode("span", { class: "login-bg h-[150px] xl:h-[250px]" })
      ], -1)),
      _createElementVNode("div", _hoisted_2, [
        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "login-logo mb-8 w-[225px]" }, null, -1)),
        (showFormType.value === FormType.LOGIN)
          ? (_openBlock(), _createElementBlock("h3", _hoisted_3, _toDisplayString(_ctx.$t('welcome')), 1))
          : _createCommentVNode("", true),
        (showFormType.value === FormType.LOGIN && showMessageUserBlocked.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_unref(ExclamationIcon), { class: "h-6 w-6 text-gray-400" })
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("p", _hoisted_8, [
                    _createTextVNode(_toDisplayString(_ctx.$t('login.userBlocked')) + " ", 1),
                    _createElementVNode("span", {
                      class: "font-medium text-yellow-700 underline hover:text-yellow-600",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (showFormType.value = FormType.RECOVER))
                    }, _toDisplayString(_ctx.$t('core.actions.clickHere')), 1)
                  ])
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        (showFormType.value === FormType.RECOVER)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("h3", _hoisted_9, _toDisplayString(_ctx.$t('forgotPassword.title')), 1),
              _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t('forgotPassword.description')), 1)
            ], 64))
          : _createCommentVNode("", true),
        (showFormType.value === FormType.SENT_RECOVER)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createElementVNode("h3", _hoisted_11, _toDisplayString(_ctx.$t('forgotPassword.requested.title')), 1),
              _createElementVNode("p", _hoisted_12, _toDisplayString(_ctx.$t('forgotPassword.requested.description')), 1)
            ], 64))
          : _createCommentVNode("", true),
        (showFormType.value === FormType.LOGIN)
          ? (_openBlock(), _createElementBlock("form", {
              key: 4,
              class: "w-full space-y-4",
              onSubmit: _withModifiers(onSubmitLogin, ["prevent"])
            }, [
              _createVNode(_component_UiTextInput, {
                modelValue: formState.email,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.email) = $event)),
                name: "email",
                type: "text",
                label: _ctx.$t('login.form.emailAddress'),
                inputClass: "text-base px-4 py-3",
                autocomplete: "email",
                errors: _unref(v$).email.$errors,
                onBlur: _unref(v$).email.$touch
              }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
              _createVNode(_component_UiTextInput, {
                modelValue: formState.password,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.password) = $event)),
                name: "password",
                type: "password",
                label: _ctx.$t('login.form.password'),
                inputClass: "text-base px-4 py-3",
                autocomplete: "password",
                errors: _unref(v$).password?.$errors,
                onBlur: _unref(v$).password?.$touch
              }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
              _createElementVNode("div", _hoisted_13, [
                _createVNode(_component_UiCheckboxInput, {
                  modelValue: formState.rememberMe,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formState.rememberMe) = $event)),
                  name: "rememberMe",
                  label: _ctx.$t('login.form.rememberMe'),
                  class: "h-6"
                }, null, 8, ["modelValue", "label"]),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("span", {
                    class: "text-primary hover:text-primary-darker cursor-pointer text-sm font-medium",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (showFormType.value = FormType.RECOVER))
                  }, _toDisplayString(_ctx.$t('login.form.forgotYourPassword')), 1)
                ])
              ]),
              _createElementVNode("div", null, [
                _createVNode(_component_UiButton, {
                  type: "submit",
                  variant: "primary",
                  class: "w-full"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", _hoisted_15, _toDisplayString(_ctx.$t('login.form.signIn')), 1)
                  ]),
                  _: 1
                })
              ])
            ], 32))
          : _createCommentVNode("", true),
        (showFormType.value === FormType.RECOVER)
          ? (_openBlock(), _createElementBlock("form", {
              key: 5,
              class: "w-full space-y-4",
              onSubmit: _withModifiers(onSubmitResetPassword, ["prevent"])
            }, [
              _createVNode(_component_UiTextInput, {
                modelValue: formState.email,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formState.email) = $event)),
                name: "email",
                type: "text",
                label: _ctx.$t('forgotPassword.form.emailAddress'),
                inputClass: "text-base px-4 py-3",
                autocomplete: "email",
                errors: _unref(v$).email.$errors,
                onBlur: _unref(v$).email.$touch
              }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("span", {
                  class: "text-primary hover:text-primary-darker cursor-pointer text-sm font-medium",
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (showFormType.value = FormType.LOGIN))
                }, _toDisplayString(_ctx.$t('core.actions.Back')), 1)
              ]),
              _createElementVNode("div", null, [
                _createVNode(_component_UiButton, {
                  type: "submit",
                  variant: "primary",
                  class: "w-full"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", _hoisted_17, _toDisplayString(_ctx.$t('forgotPassword.form.resetPass')), 1)
                  ]),
                  _: 1
                })
              ])
            ], 32))
          : _createCommentVNode("", true),
        (showFormType.value === FormType.SENT_RECOVER)
          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
              _createVNode(_component_UiButton, {
                variant: "primary",
                class: "w-full",
                onClick: _cache[7] || (_cache[7] = ($event: any) => (showFormType.value = FormType.LOGIN))
              }, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_19, _toDisplayString(_ctx.$t('forgotPassword.requested.action')), 1)
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(_component_UiLoadingDialog, {
      show: _unref(uiStore).isLoading,
      text: _ctx.$t('ui-loading-dialog-text')
    }, null, 8, ["show", "text"])
  ], 64))
}
}

})